import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { ConfirmModal } from 'src/components/ConfirmModal';
import {
  useChecklistSyncStatus,
  useSyncTenantChecklistMutation,
} from 'src/queries/checklist/useChecklistSync';
import { isChecklistSyncInFlight } from 'src/services/checklist/syncTypes';
import Button from 'src/components/Button';
import { cn } from 'src/utils/cn';

interface ChecklistSyncButtonProps {
  readonly checklistId: string;
  readonly checklistName: string;
  readonly isGlobal: boolean;
  readonly disabled?: boolean;
  /** 'compact' matches the smaller buttons used inside grid rows. */
  readonly size?: 'default' | 'compact';
}

/**
 * Pushes a global checklist's rules down onto every loan that already carries a
 * copy of it. The sync runs asynchronously, so this polls for progress and
 * stays disabled while a run is in flight.
 */
export const ChecklistSyncButton = ({
  checklistId,
  checklistName,
  isGlobal,
  disabled = false,
  size = 'default',
}: ChecklistSyncButtonProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const syncStatusQuery = useChecklistSyncStatus(checklistId, isGlobal);
  const syncMutation = useSyncTenantChecklistMutation();

  const inFlight = isChecklistSyncInFlight(syncStatusQuery.data);
  const isDisabled =
    disabled || !isGlobal || !checklistId || inFlight || syncMutation.isPending;

  const handleConfirm = () => {
    setConfirmOpen(false);
    syncMutation.mutate(
      { checklistId },
      {
        onSuccess: (data) => {
          if (!data.success) {
            toast.error(data.message ?? 'Failed to start checklist sync');
            return;
          }
          toast.success(
            data.alreadyRunning
              ? 'A sync is already running for this checklist.'
              : 'Checklist sync started. Existing loans are being updated.',
          );
          void syncStatusQuery.refetch();
        },
        onError: (error) => {
          toast.error(error.message ?? 'Failed to start checklist sync');
        },
      },
    );
  };

  const label = inFlight ? 'Syncing…' : 'Sync to loans';

  return (
    <>
      <Button
        variant="outline"
        type="button"
        id={`sync-checklist-${checklistId}`}
        data-testid={`sync-checklist-${checklistId}`}
        disabled={isDisabled}
        onClick={() => setConfirmOpen(true)}
        title={
          isGlobal
            ? 'Apply this checklist to loans that already have a copy of it'
            : 'Only global checklists can be synced to loans'
        }
        className={cn(
          'items-center gap-1.5 whitespace-nowrap shrink-0',
          size === 'compact' && 'py-1.5 px-3 text-xs rounded-sm',
        )}
        icon={
          <RefreshCw
            className={cn(
              size === 'compact' ? 'size-3' : 'size-4',
              inFlight && 'animate-spin',
            )}
          />
        }
      >
        {label}
      </Button>

      <ConfirmModal
        open={confirmOpen}
        title="Sync checklist to existing loans?"
        confirmButtonText="Sync to loans"
        confirmButtonClassNames="bg-azure-radiance hover:bg-curious-blue text-white"
        confirmIconBackground="bg-blue-100"
        confirmIcon={
          <RefreshCw aria-hidden="true" className="size-6 text-blue-600" />
        }
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      >
        <p>
          Every loan that already has a copy of{' '}
          <span className="font-semibold">{checklistName}</span> will be updated
          to match this checklist.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Published rules missing from a loan are added to it.</li>
          <li>
            Rules that changed are overwritten, and any result already recorded
            for them is cleared — those rules read as Pending until the loan is
            evaluated again.
          </li>
          <li>Rules created directly on a loan are left untouched.</li>
        </ul>
      </ConfirmModal>
    </>
  );
};

export default ChecklistSyncButton;
